











import { Component, Mixins } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { Auth } from "@/mixins/auth";
import { User } from "@/models/user/user";
import { StoreModule } from "@/store/types";
import { GlobalActions } from "@/store/modules/global/types";
import { UserActions } from "@/store/modules/user/types";
import { OrderActions } from "@/store/modules/order/types";
import { PartialNetSuiteCustomer, PriceLevel } from "@/models/netsuite/netsuite";

@Component
export default class ImpersonationBanner extends Mixins(Auth) {
  @(namespace(StoreModule.Order).Action(OrderActions.ResetForUserChange))
  resetOrderForUserChange!: () => Promise<void>;

  @(namespace(StoreModule.Global).Action(GlobalActions.AddMultiplier))
  setMultiplier!: (value: PriceLevel | null) => Promise<void>;

  @(namespace(StoreModule.Global).Action(GlobalActions.AddCustomers))
  setCustomers!: (value: PartialNetSuiteCustomer[]) => Promise<void>;

  @(namespace(StoreModule.Global).Action(GlobalActions.AddPriceLevels))
  setPriceLevels!: (value: PriceLevel[]) => Promise<void>;

  @(namespace(StoreModule.User).Action(UserActions.AddUser))
  setUser!: (value: User | null) => Promise<void>;

  /**
   * Everything the impersonated session touched has to go, not just the auth
   * token: the order module is persisted to localStorage, so a cart, a
   * selected customer or a draft/NetSuite edit reference left behind would
   * reappear under whoever the browser lands on next — the impersonator, or
   * the next person impersonated. The order reset is the important one; the
   * edit-mode banner keys off draft_id, and it is what survived before.
   *
   * The NetSuite caches (customers, price levels, multiplier) are scoped to
   * the impersonated rep, so they are dropped here too rather than waiting for
   * AuthCallback to refetch. The PSQL drafts themselves are untouched — they
   * stay in their owner's Saved Quotes.
   */
  async leaveImpersonation(): Promise<void> {
    const leaveUrl = this.impersonation?.leave_url;

    // Order state first: it is the one that outlives the tab.
    await this.resetOrderForUserChange();
    await this.setMultiplier(null);
    await this.setCustomers([]);
    await this.setPriceLevels([]);

    await this.setAuthToken(null);
    await this.setLoggedIn(false);
    await this.setUserRole(null);
    await this.setImpersonation(null);
    // null, not {}: AuthCallback's user-change guard treats an empty object as
    // a previous user with no id/email and skips its own reset.
    await this.setUser(null);

    // vuex-persist writes synchronously on commit, so the reset above is
    // already in localStorage. Dropping the key outright covers anything a
    // previous build persisted under it that no current mutation clears.
    window.localStorage.removeItem("orderForm");

    if (leaveUrl) {
      window.location.href = leaveUrl;
    }
  }
}
